import { ColorInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useRef } from "react";
import styled from "styled-components";
import { FormPanel } from "../../common/Form/FormRow";
import Container from "../../common/Container";
import PageHeader from "../../common/PageHeader";
import PlusIcon from "../../common/Icons/PlusIcon";
import SaveIcon from "../../common/Icons/SaveIcon";
import { CustomMultiSelect } from "../../common/Inputs/MultiSelectInput";
import { useAuth } from "../../../context/AuthContext";
import { validateRequired } from "../../../utils/validations";
import {
  creationNotification,
  updateNotification,
  errorNotification,
} from "../../../utils/useNotification";
import { createTag, updateTagById, TagKind } from "../../../api/tagApi";
import { unwrap } from "../../../utils/apiResult";

const KIND_LABELS: Record<TagKind, string> = {
  role: "Rol",
  skill: "Yetenek",
};

interface Props {
  kind: TagKind;
  /** Verilirse düzenleme modu; verilmezse yeni kayıt. */
  initialTag?: any;
  onSaved?: () => void;
}

/**
 * Rol ve yeteneğin tek formu.
 * Eski NewRole / NewSkill / RoleUpdateForm / SkillUpdateForm dosyalarının yerine geçer.
 *
 * Etiketin departman/kontrat atadığı "paket" (bundle) alanı 1 Eyl'de kaldırıldı:
 * rol/yetenek yalnızca kendini anlatır, departman ve kontrat personel bazında seçilir.
 */
const TagForm = ({ kind, initialTag, onSaved }: Props) => {
  const { tagSelectData, refetchTagSelectData } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const isEdit = Boolean(initialTag?._id);
  const label = KIND_LABELS[kind];

  const form = useForm<any>({
    initialValues: {
      name: initialTag?.name ?? "",
      description: initialTag?.description ?? "",
      color: initialTag?.color ?? "",
      // `getTagById` ham kaydı döndürüyor: içinde `relatedTagIds` var, listedeki
      // gibi zenginleştirilmiş `relatedTags` YOK. Yalnızca `relatedTags`'e
      // bakıldığı için düzenleme formu alanı boş açılıyor ve kaydedince mevcut
      // ilişkileri siliyordu; iki şekli de kabul ediyoruz.
      relatedTagIds: (
        initialTag?.relatedTagIds ??
        initialTag?.relatedTags ??
        []
      ).map((t: any) => String(t?._id ?? t?.value ?? t)),
    },
    validate: {
      name: validateRequired,
    },
  });

  const handleSubmit = async (values: any) => {
    const payload = {
      name: values.name,
      kind,
      // Boş string gönderilir, undefined DEĞİL: axios undefined alanı gövdeden
      // atıyor, o zaman düzenleme ucu eski değeri koruyor ve dolu bir açıklama
      // ya da renk bir daha temizlenemiyordu.
      description: values.description ?? "",
      color: values.color ?? "",
      relatedTagIds: kind === "role" ? values.relatedTagIds : [],
    };

    const result = unwrap(
      isEdit
        ? await updateTagById({ id: initialTag._id, updates: payload })
        : await createTag(payload)
    );

    if (result.ok) {
      isEdit ? updateNotification(true) : creationNotification(label, true);
      refetchTagSelectData();
      if (!isEdit) form.reset();
      onSaved?.();
      return;
    }

    errorNotification({
      message: result.message ?? `${label} kaydedilirken bir hata oluştu.`,
    });
  };

  return (
    <Container>
      <PageHeader
        pageTitle={isEdit ? `${label} Düzenle` : `Yeni ${label} Kaydı`}
        isButtoned
        buttonText={isEdit ? "Kaydet" : "Oluştur"}
        buttonIcon={isEdit ? <SaveIcon /> : <PlusIcon />}
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />

      <StyledForm ref={formRef} onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label={`${label} İsmi`}
          placeholder={`${label} ismi giriniz.`}
          radius={10}
          maxLength={40}
          size="md"
          {...form.getInputProps("name")}
        />
        <TextInput
          label={`${label} Açıklaması`}
          placeholder={`${label} açıklaması giriniz.`}
          radius={10}
          maxLength={150}
          size="md"
          {...form.getInputProps("description")}
        />
        <ColorInput
          label="Rozet Rengi"
          placeholder="Varsayılan renk kullanılsın"
          radius={10}
          size="md"
          format="hex"
          {...form.getInputProps("color")}
        />

        {kind === "role" && (
          <CustomMultiSelect
            label="Gerekli Yetenekler"
            searchable
            placeholder="Yetenek seçiniz."
            w={"100%"}
            data={tagSelectData.skill}
            {...form.getInputProps("relatedTagIds")}
          />
        )}
      </StyledForm>
    </Container>
  );
};

export default TagForm;

const StyledForm = styled(FormPanel).attrs({ $width: 448 })`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
